import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { apiService } from '../services/apiService';
import { bookingStudioOf } from '../utils/studioOf';

/** The slice of a booking this dialog reads — both the Bookings page rows and
 *  the schedule calendar's attendee rows satisfy it. */
export interface StatusUpdatableBooking {
  _id: string;
  bookingId?: string;
  bookingStatus: string;
  user?: { name?: string };
  transaction?: { modeOfPayment?: string };
}

export const isPackBooking = (b?: { transaction?: { modeOfPayment?: string } } | null): boolean =>
  b?.transaction?.modeOfPayment === 'subscription';

// The credit-back default the status picker applies when a status is chosen.
const creditDefaultFor = (status: string) => status === 'CANCELLED';

interface BookingStatusDialogProps {
  open: boolean;
  booking: StatusUpdatableBooking | null;
  /** Status preselected on open (e.g. CHECKED_IN from a "Check in" button).
   *  Omitted = the booking's current status. */
  initialStatus?: string;
  onClose: () => void;
  onUpdated: (bookingId: string, newStatus: string) => void;
  onError: (message: string) => void;
}

/**
 * The one "Update Booking Status" flow — check-in, no-show, cancel and restore
 * all go through here, with the pack credit-back / deduct choice. Shared by the
 * Bookings page and the schedule calendar so both behave identically.
 */
const BookingStatusDialog: React.FC<BookingStatusDialogProps> = ({
  open,
  booking,
  initialStatus,
  onClose,
  onUpdated,
  onError,
}) => {
  const [newStatus, setNewStatus] = useState('');
  const [statusUpdateReason, setStatusUpdateReason] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [creditClassBack, setCreditClassBack] = useState(true);

  useEffect(() => {
    if (!open || !booking) return;
    setNewStatus(initialStatus ?? booking.bookingStatus);
    setStatusUpdateReason('');
    setCreditClassBack(initialStatus ? creditDefaultFor(initialStatus) : true);
  }, [open, booking, initialStatus]);

  const handleStatusUpdateConfirm = async () => {
    if (!booking || !newStatus) return;
    setIsUpdatingStatus(true);
    try {
      const sendCreditClassBack =
        newStatus === 'CANCELLED' && isPackBooking(booking)
          ? creditClassBack
          : undefined;
      await apiService.updateBookingStatus(booking._id, newStatus, statusUpdateReason || undefined, sendCreditClassBack, bookingStudioOf(booking));
      onUpdated(booking._id, newStatus);
    } catch (err: any) {
      onError(err.message || 'Failed to update booking status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Update Booking Status</DialogTitle>
      <DialogContent>
        {booking && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Booking: <strong>{booking.bookingId || booking._id}</strong>
              <br />User: <strong>{booking.user?.name}</strong>
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                label="New Status"
                onChange={(e) => {
                  const val = e.target.value;
                  setNewStatus(val);
                  if (val === 'CANCELLED') setCreditClassBack(true);
                  else if (val === 'BOOKED' || val === 'CHECKED_IN') setCreditClassBack(false);
                }}
              >
                <MenuItem value="BOOKED">Booked</MenuItem>
                <MenuItem value="CHECKED_IN">Checked In</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                <MenuItem value="NO_SHOW">No Show</MenuItem>
              </Select>
            </FormControl>
            {newStatus === 'CANCELLED' &&
              booking.bookingStatus !== 'CANCELLED' &&
              isPackBooking(booking) && (
              <Box sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1, p: 1.5, bgcolor: 'warning.50' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={creditClassBack}
                      onChange={(e) => setCreditClassBack(e.target.checked)}
                      color="warning"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Credit 1 class back to user's pack
                    </Typography>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  Check this if the booking used a pack class that should be returned (e.g. accidental check-in).
                </Typography>
              </Box>
            )}
            {booking.bookingStatus === 'CANCELLED' &&
              (newStatus === 'BOOKED' || newStatus === 'CHECKED_IN') &&
              isPackBooking(booking) && (
              <Box sx={{ border: '1px solid', borderColor: 'info.main', borderRadius: 1, p: 1.5, bgcolor: 'info.50' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={creditClassBack}
                      onChange={(e) => setCreditClassBack(e.target.checked)}
                      color="info"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Deduct 1 class from user's pack
                    </Typography>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  Check this if the class was previously credited back when the booking was cancelled.
                </Typography>
              </Box>
            )}
            <TextField
              label="Reason (optional)"
              value={statusUpdateReason}
              onChange={(e) => setStatusUpdateReason(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={2}
              placeholder="Reason for status change..."
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleStatusUpdateConfirm}
          variant="contained"
          disabled={isUpdatingStatus || !newStatus}
        >
          {isUpdatingStatus ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingStatusDialog;
